/**
 * CustomerInfoForm.jsx — Booking form with Square Web Payments SDK
 *
 * Step 2 of the public booking flow. Collects customer name, email, phone,
 * then shows the Square card form for payment. On submit, tokenizes the card
 * and calls the createBooking Cloud Function which processes the Square payment,
 * creates the booking in Firestore, syncs to Square CRM, and sends confirmation emails.
 *
 * Props:
 *   card    {Object}   — Firestore class card
 *   session {Object}   — selected session from SessionPicker
 *   seats   {number}   — number of seats
 *   accent  {string}   — accent color hex
 *   onSuccess {Function} — called with { bookingId, customerName, customerEmail } on success
 *   onBack  {Function} — return to session picker
 */
import { useState, useEffect, useRef } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

const APP_ID = import.meta.env.VITE_SQUARE_APP_ID;
const LOCATION_ID = import.meta.env.VITE_SQUARE_LOCATION_ID;

function fmtTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hr = h % 12 || 12;
  return `${hr}:${String(m).padStart(2, '0')} ${ampm}`;
}

/* Email format validation — MUST match the backend rule in functions/index.js
   (isValidEmailFormat) exactly. Catches the common "missing TLD" typo
   (e.g. "user@gmail") that HTML5 email validation lets through. */
function isValidEmailFormat(email) {
  if (!email || typeof email !== 'string') return false;
  const trimmed = email.trim();
  if (trimmed.length > 254) return false;
  return /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/.test(trimmed);
}

/* Extract name / email / phone from a Square wallet tokenization result.
   Square returns contact data in either result.details.shipping.contact OR
   result.details.billing depending on flow, so try both. Returns trimmed
   strings (empty when missing). */
function extractWalletContact(details) {
  const sc = details?.shipping?.contact ?? {};
  const bc = details?.billing ?? {};
  const givenName = sc.givenName || bc.givenName || '';
  const familyName = sc.familyName || bc.familyName || '';
  const fullName = `${givenName} ${familyName}`.trim();
  return {
    name: fullName,
    email: (sc.email || bc.email || '').trim(),
    phone: (sc.phone || bc.phone || '').trim(),
  };
}

export default function CustomerInfoForm({
  card,
  session,
  seats,
  accent,
  onSuccess,
  onBack,
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [smsConsent, setSmsConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  /* Live email format check used to:
     - Gate the pay buttons (card, Apple Pay, Google Pay)
     - Show an inline error under the email field once the user has typed
       something (so an empty form doesn't immediately show a red error)
     Uses the same regex as the backend so the two NEVER disagree. */
  const trimmedEmail = email.trim();
  const emailIsValid =
    trimmedEmail.length > 0 && isValidEmailFormat(trimmedEmail);
  const emailHasTypo = trimmedEmail.length > 0 && !emailIsValid;

  // Square Web Payments SDK state
  const [sdkReady, setSdkReady] = useState(false);
  const [applePayReady, setApplePayReady] = useState(false);
  const [googlePayReady, setGooglePayReady] = useState(false);
  const cardRef = useRef(null);
  const appleRef = useRef(null);
  const googlePayRef = useRef(null);

  const total = (session.price || 0) * seats;

  const dateObj = new Date(session.date + 'T12:00:00');
  const formattedDate = dateObj.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });

  // Initialize Square Web Payments SDK
  useEffect(() => {
    if (!window.Square || !APP_ID || !LOCATION_ID) return;
    let alive = true;

    async function init() {
      try {
        const payments = window.Square.payments(APP_ID, LOCATION_ID);

        // Card form
        const cardInstance = await payments.card({
          style: {
            '.input-container': {
              borderColor: '#DDD8CC',
              borderRadius: '8px',
            },
            '.input-container.is-focus': { borderColor: '#9C7A3C' },
            '.message-text': { color: '#5a4a30' },
          },
        });
        await cardInstance.attach('#square-card-container');
        if (alive) {
          cardRef.current = cardInstance;
          setSdkReady(true);
        }

        // Apple Pay
        if (total > 0) {
          try {
            const req = payments.paymentRequest({
              countryCode: 'US',
              currencyCode: 'USD',
              total: {
                amount: String(total.toFixed(2)),
                label:
                  seats > 1
                    ? `${card.title} x${seats}`
                    : card.title || 'Class Booking',
              },
              // Ask the wallet to share name/email/phone so the customer can
              // skip typing them. The wallet still lets them edit which fields
              // to share before confirming.
              requestBillingContact: true,
              requestShippingContact: true,
            });
            const apple = await payments.applePay(req);
            if (alive) {
              appleRef.current = apple;
              setApplePayReady(true);
            }
          } catch {
            // Apple Pay not available on this device/browser
          }

          // Google Pay (skip on Safari)
          const isSafari = /^((?!chrome|android).)*safari/i.test(
            navigator.userAgent,
          );
          if (!isSafari) {
            try {
              const gReq = payments.paymentRequest({
                countryCode: 'US',
                currencyCode: 'USD',
                total: {
                  amount: String(total.toFixed(2)),
                  label:
                    seats > 1
                      ? `${card.title} x${seats}`
                      : card.title || 'Class Booking',
                },
                // See Apple Pay note above
                requestBillingContact: true,
                requestShippingContact: true,
              });
              const goog = await payments.googlePay(gReq);
              await goog.attach('#google-pay-button', {
                buttonColor: 'black',
                buttonType: 'book',
                buttonSizeMode: 'fill',
              });
              if (alive) {
                googlePayRef.current = goog;
                setGooglePayReady(true);

                goog.addEventListener('ontokenization', (event) => {
                  const { tokenResult } = event.detail;
                  if (tokenResult.status === 'OK') {
                    // Pull name / email / phone from the wallet — handlePayment
                    // does the merge with typed fields + validation.
                    const walletContact = extractWalletContact(
                      tokenResult.details,
                    );
                    handlePayment(tokenResult.token, walletContact);
                  } else {
                    setError(
                      tokenResult.errors?.map((e) => e.message).join(' ') ||
                        'Google Pay failed.',
                    );
                  }
                });
              }
            } catch {
              // Google Pay not available
            }
          }
        }
      } catch (err) {
        console.error('Square SDK init failed:', err);
      }
    }

    init();

    return () => {
      alive = false;
      cardRef.current?.destroy?.();
      appleRef.current = null;
      googlePayRef.current?.destroy?.();
      setApplePayReady(false);
      setGooglePayReady(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Core payment handler — single source of truth for the createBooking call.
     Takes optional wallet contact (from Apple Pay / Google Pay) and merges it
     with the typed form fields:
       email → wallet's if valid, else typed
       name  → wallet's if present, else typed
       phone → wallet's if present, else typed
     Validates the final values before calling the backend. The backend has
     its own validation (defense in depth), but failing fast on the client
     gives the customer a clearer error. */
  async function handlePayment(sourceId, walletContact = null) {
    const wc = walletContact || {};

    const finalEmail = isValidEmailFormat(wc.email)
      ? wc.email.trim()
      : email.trim();
    const finalName = (wc.name || name).trim();
    const finalPhone = (wc.phone || phone).trim();

    // Validate final values before we hit the backend
    if (!finalName) {
      setError(
        wc.name === undefined && !name.trim()
          ? 'Please enter your name above before paying.'
          : "We didn't get your name. Please enter it above and try again.",
      );
      return;
    }
    if (!isValidEmailFormat(finalEmail)) {
      setError(
        finalEmail
          ? 'Please check your email address — it looks incomplete (e.g. missing ".com").'
          : "We didn't get your email. Please enter it above and try again.",
      );
      return;
    }

    // Reflect wallet-provided fields back into the form so the customer sees
    // exactly what we'll record on the booking (no surprises after the fact).
    if (wc.name && wc.name !== name) setName(wc.name);
    if (wc.email && isValidEmailFormat(wc.email) && wc.email !== email)
      setEmail(wc.email);
    if (wc.phone && wc.phone !== phone) setPhone(wc.phone);

    setSubmitting(true);
    setError(null);
    try {
      const createBookingFn = httpsCallable(functions, 'createBooking');
      const { data } = await createBookingFn({
        cardId: card.id,
        sessionId: session.id,
        seats,
        customerName: finalName,
        customerEmail: finalEmail,
        customerPhone: finalPhone,
        smsConsent: !!(finalPhone && smsConsent),
        sourceId,
      });

      if (data?.success) {
        onSuccess({
          bookingId: data.bookingId,
          customerName: finalName,
          customerEmail: finalEmail,
        });
      } else {
        setError('Booking failed. Please try again.');
      }
    } catch (err) {
      console.error('Booking error:', err);
      setError('Payment failed. Please try again or contact us for help.');
    } finally {
      setSubmitting(false);
    }
  }

  // Card payment handler — cards don't provide contact info, so we rely
  // entirely on the typed fields (already validated by button gating).
  async function handleCardPay(e) {
    e.preventDefault();
    if (!cardRef.current || !name.trim() || !email.trim() || !emailIsValid)
      return;

    setSubmitting(true);
    setError(null);
    try {
      const result = await cardRef.current.tokenize();
      if (result.status === 'OK') {
        await handlePayment(result.token);
      } else {
        setError(
          result.errors?.map((e) => e.message).join(' ') || 'Card error.',
        );
        setSubmitting(false);
      }
    } catch {
      setError('Payment failed. Please try again.');
      setSubmitting(false);
    }
  }

  // Apple Pay handler — wallet provides name / email / phone via requestBilling
  // & requestShipping contact (set in paymentRequest above). handlePayment
  // merges wallet contact with any typed fallback values and validates.
  async function handleApplePay() {
    if (!appleRef.current) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await appleRef.current.tokenize();
      if (result.status === 'OK') {
        const walletContact = extractWalletContact(result.details);
        await handlePayment(result.token, walletContact);
      } else {
        setError(
          result.errors?.map((e) => e.message).join(' ') || 'Apple Pay failed.',
        );
      }
    } catch {
      setError('Apple Pay was cancelled.');
    } finally {
      setSubmitting(false);
    }
  }

  const sdkMissing = !APP_ID || !LOCATION_ID;

  return (
    <div style={styles.container}>
      {/* Booking summary */}
      <div style={{ ...styles.summary, borderColor: `${accent}44` }}>
        <div style={styles.summaryLabel}>YOUR BOOKING</div>
        <div style={styles.summaryTitle}>{card.title}</div>
        <div style={styles.summaryRow}>
          <span>{formattedDate}</span>
          <span style={styles.sep}>-</span>
          <span>
            {fmtTime(session.startTime)} – {fmtTime(session.endTime)}
          </span>
        </div>
        <div style={styles.summaryRow}>
          <span>
            {seats} seat{seats !== 1 ? 's' : ''}
          </span>
          {total > 0 && (
            <>
              <span style={styles.sep}>-</span>
              <span style={{ color: accent, fontWeight: 700 }}>
                ${total} total
              </span>
            </>
          )}
        </div>
        {session.location && (
          <div style={{ ...styles.summaryRow, color: '#9C7A3C' }}>
            <a
              href={`https://maps.google.com/?q=${encodeURIComponent(session.location)}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#9C7A3C', textDecoration: 'none' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.textDecoration = 'underline';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.textDecoration = 'none';
              }}
            >
              {session.location}
            </a>
          </div>
        )}
      </div>

      {/* Customer info + payment form */}
      <form onSubmit={handleCardPay} style={styles.form}>
        <div style={styles.header}>Your Details</div>

        <div style={styles.field}>
          <label style={styles.label}>Full Name *</label>
          <input
            type="text"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Jane Smith"
            style={styles.input}
          />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Email *</label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="jane@example.com"
            style={{
              ...styles.input,
              ...(emailHasTypo ? styles.inputInvalid : {}),
            }}
          />
          {emailHasTypo && (
            <div style={styles.fieldError}>
              That looks incomplete — make sure it ends with something like
              <strong> .com</strong>.
            </div>
          )}
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Phone</label>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="(555) 000-0000"
            style={styles.input}
          />
        </div>

        {phone.trim() && (
          <label style={styles.consent}>
            <input
              type="checkbox"
              checked={smsConsent}
              onChange={(e) => setSmsConsent(e.target.checked)}
              style={styles.consentCheckbox}
            />
            <span style={styles.consentText}>
              I agree to receive SMS updates about my booking (confirmations,
              reminders, and changes) from Viri&apos;s Clayworks. Msg &amp; data
              rates may apply. Msg frequency varies. Reply STOP to opt out, HELP
              for help.
            </span>
          </label>
        )}

        {/* Square Card Form */}
        {total > 0 && (
          <>
            <div style={{ ...styles.header, marginTop: 12 }}>Payment</div>

            {sdkMissing ? (
              <div style={styles.sdkError}>
                Payment not configured — Square env vars required.
              </div>
            ) : (
              <>
                {/* Apple Pay button */}
                {applePayReady && (
                  <button
                    type="button"
                    onClick={handleApplePay}
                    // Intentionally NOT gated on typed name/email — Apple Pay
                    // can provide them. handlePayment falls back to typed
                    // fields if the wallet doesn't share, and shows a clear
                    // error if neither has what's needed.
                    disabled={submitting}
                    style={{
                      WebkitAppearance: '-apple-pay-button',
                      ApplePayButtonStyle: 'black',
                      width: '100%',
                      height: 44,
                      border: 'none',
                      borderRadius: 8,
                      cursor: submitting ? 'default' : 'pointer',
                      marginBottom: 8,
                      opacity: submitting ? 0.6 : 1,
                    }}
                  />
                )}

                {/* Google Pay button — same logic as Apple Pay: NOT gated on
                    typed fields because the wallet provides name/email/phone.
                    Only blocked while a submission is already in flight. */}
                <div
                  id="google-pay-button"
                  style={{
                    width: '100%',
                    height: googlePayReady ? 44 : 0,
                    marginBottom: googlePayReady ? 8 : 0,
                    overflow: 'hidden',
                    opacity: submitting ? 0.5 : 1,
                    pointerEvents: submitting ? 'none' : 'auto',
                  }}
                />

                {/* Divider between digital wallets and card */}
                {(applePayReady || googlePayReady) && (
                  <div style={styles.orDivider}>
                    <div style={styles.orLine} />
                    <span style={styles.orText}>or pay with card</span>
                    <div style={styles.orLine} />
                  </div>
                )}

                {/* Square card input */}
                <div
                  id="square-card-container"
                  style={{ marginBottom: 12, minHeight: 40 }}
                />

                <div style={styles.securedNote}>
                  <svg
                    width="12"
                    height="14"
                    viewBox="0 0 12 14"
                    fill="none"
                    stroke="#9C7A3C"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="1" y="6" width="10" height="7" rx="1" />
                    <path d="M3 6V4a3 3 0 0 1 6 0v2" />
                  </svg>
                  <span>Secured by Square - SSL encrypted</span>
                </div>
              </>
            )}
          </>
        )}

        {error && <p style={styles.error}>{error}</p>}

        <div style={styles.actions}>
          <button type="button" onClick={onBack} style={styles.backBtn}>
            ← Back
          </button>
          <button
            type="submit"
            disabled={
              submitting ||
              !name.trim() ||
              !email.trim() ||
              !emailIsValid ||
              (total > 0 && (!sdkReady || sdkMissing))
            }
            style={{
              ...styles.submitBtn,
              background:
                submitting || (total > 0 && !sdkReady) ? '#ccc' : accent,
              cursor:
                submitting || (total > 0 && !sdkReady) ? 'default' : 'pointer',
            }}
          >
            {submitting
              ? 'Processing...'
              : total > 0
                ? `Pay $${total}`
                : 'Confirm Booking'}
          </button>
        </div>
      </form>
    </div>
  );
}

const styles = {
  container: {
    padding: '36px 40px',
    display: 'flex',
    flexDirection: 'column',
    gap: 28,
  },
  summary: {
    border: '1.5px solid',
    borderRadius: 12,
    padding: '20px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    background: '#FDFAF5',
  },
  summaryLabel: {
    fontFamily: "'Raleway', sans-serif",
    fontWeight: 700,
    fontSize: '0.65rem',
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    color: '#9C7A3C',
    marginBottom: 4,
  },
  summaryTitle: {
    fontFamily: "'Raleway', sans-serif",
    fontWeight: 700,
    fontSize: '1rem',
    color: '#1a1208',
    textTransform: 'uppercase',
    letterSpacing: '0.02em',
  },
  summaryRow: {
    fontFamily: "'Raleway', sans-serif",
    fontWeight: 700,
    fontSize: '0.8rem',
    color: '#5a4a30',
    display: 'flex',
    gap: 6,
    flexWrap: 'wrap',
  },
  sep: { color: '#DDD8CC' },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  header: {
    fontFamily: "'Raleway', sans-serif",
    fontWeight: 700,
    fontSize: '0.7rem',
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    color: '#9C7A3C',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  label: {
    fontFamily: "'Raleway', sans-serif",
    fontWeight: 700,
    fontSize: '0.72rem',
    color: '#5a4a30',
    letterSpacing: '0.05em',
  },
  input: {
    fontFamily: "'Raleway', sans-serif",
    fontSize: '0.9rem',
    color: '#1a1208',
    background: '#F9F2E9',
    border: '1.5px solid #DDD8CC',
    borderRadius: 8,
    padding: '10px 14px',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  },
  error: {
    fontFamily: "'Raleway', sans-serif",
    fontSize: '0.8rem',
    color: '#B55A3A',
    margin: 0,
    padding: '10px 14px',
    background: '#fdf0f0',
    border: '1px solid #e8c8c8',
    borderRadius: 8,
  },
  inputInvalid: {
    borderColor: '#e8a8a8',
    background: '#fffafa',
  },
  fieldError: {
    fontFamily: "'Raleway', sans-serif",
    fontSize: '0.75rem',
    color: '#B55A3A',
    marginTop: 6,
    lineHeight: 1.4,
  },
  sdkError: {
    fontFamily: "'Raleway', sans-serif",
    fontSize: '0.82rem',
    color: '#B55A3A',
    padding: '14px 16px',
    background: '#fdf0f0',
    border: '1px solid #e8c8c8',
    borderRadius: 8,
    textAlign: 'center',
  },
  actions: {
    display: 'flex',
    gap: 12,
    alignItems: 'center',
    marginTop: 8,
  },
  backBtn: {
    fontFamily: "'Raleway', sans-serif",
    fontWeight: 700,
    fontSize: '0.8rem',
    color: '#9C7A3C',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
  },
  submitBtn: {
    fontFamily: "'Raleway', sans-serif",
    fontWeight: 700,
    fontSize: '0.95rem',
    color: '#fff',
    border: 'none',
    borderRadius: 50,
    padding: '13px 28px',
    flex: 1,
    transition: 'opacity 0.2s',
  },
  orDivider: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    margin: '4px 0',
  },
  orLine: {
    flex: 1,
    height: 1,
    background: '#DDD8CC',
  },
  orText: {
    fontFamily: "'Raleway', sans-serif",
    fontWeight: 700,
    fontSize: '0.7rem',
    color: '#9C7A3C',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    whiteSpace: 'nowrap',
  },
  consent: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    cursor: 'pointer',
    marginTop: -4,
  },
  consentCheckbox: {
    marginTop: 3,
    accentColor: '#9C7A3C',
    cursor: 'pointer',
    flexShrink: 0,
  },
  consentText: {
    fontFamily: "'Raleway', sans-serif",
    fontSize: '0.72rem',
    color: '#5a4a30',
    lineHeight: 1.45,
  },
  securedNote: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    justifyContent: 'center',
    fontFamily: "'Raleway', sans-serif",
    fontSize: '0.68rem',
    color: '#9C7A3C',
    fontWeight: 600,
    letterSpacing: '0.03em',
  },
};
